import { createContext, useContext, useState, useEffect, useCallback, ReactNode, useMemo } from 'react';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { AuthError, type AccountInfo } from '@azure/msal-browser';
import { apiRequest } from '@/config/authConfig';
import apiClient, { setTokenProvider } from '@/services/api/apiClient';
import { getApiErrorMessage } from '@/utils/helpers/apiError.helper';
import type { User, UserProfile } from '../types/user';

/** MSAL error codes for a request that never reached Azure AD. */
const NETWORK_AUTH_ERROR_CODES = new Set(['no_network_connectivity', 'post_request_failed']);

const isNetworkAuthError = (error: unknown): boolean =>
	error instanceof AuthError && NETWORK_AUTH_ERROR_CODES.has(error.errorCode);

/**
 * The sentence shown when /api/users/me fails for good.
 *
 * For an Azure AD token the API turns away an unknown user, an inactive one and a sign-in from an
 * unregistered tenant alike, with a bare 401 (the reason stays in the server log), so the client
 * can't tell them apart and names them together.
 */
function describeProfileError(err: unknown): string {
	const response = (err as { response?: { status: number } }).response;

	if (!response) {
		return "We can't reach the server right now. Check your internet connection and try again.";
	}

	const reason = getApiErrorMessage(err, '');

	if (response.status === 404) {
		return "Your account isn't set up in Dataloop yet. Ask your administrator to add you, then try again.";
	}

	if (response.status === 401 || response.status === 403) {
		return reason
			? `Your account doesn't have access to Dataloop: ${reason} Contact your administrator if you think this is a mistake.`
			: "We couldn't verify your account. It may be deactivated, not set up in Dataloop yet, or signed in from another organisation. Contact your administrator.";
	}

	if (response.status >= 500) {
		return 'The server ran into a problem while loading your account. Try again in a moment.';
	}

	return reason || 'Something went wrong while loading your account. Try again, or log out and sign in once more.';
}

/**
 * Authentication Context
 * Provides Azure AD authentication state and methods
 */
interface AuthContextType {
	isAuthenticated: boolean;
	isLoading: boolean;
	error: string | null;
	user: User | null;
	account: AccountInfo | null;
	userProfile: UserProfile | null;
	login: () => Promise<void>;
	logout: () => Promise<void>;
	getAccessToken: () => Promise<string | null>;
	refreshUserProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
	children: ReactNode;
}

/**
 * Authentication Provider Component
 * Manages Azure AD authentication and user profile from backend API
 */
export function AuthProvider({ children }: AuthProviderProps) {
	const { instance, accounts } = useMsal();
	const isAuthenticated = useIsAuthenticated();
	const [isLoading, setIsLoading] = useState(true);
	const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
	const [error, setError] = useState<string | null>(null);

	const account = accounts[0] || null;

	/**
	 * Get access token for API calls
	 * Gets account directly from MSAL instance to avoid stale closure issues
	 */
	const getAccessToken = useCallback(async (): Promise<string | null> => {
		const currentAccount = instance.getAllAccounts()[0] || null;

		if (!currentAccount) {
			console.error('❌ [AuthContext] No account found!');
			return null;
		}

		try {
			const response = await instance.acquireTokenSilent({
				...apiRequest,
				account: currentAccount,
			});
			return response.accessToken;
		} catch (error) {
			console.error('❌ [AuthContext] Failed to acquire token silently:', error);

			// A lost connection is the one failure where signing in again can't help and would throw away
			// unsaved work: fail this request as a connection error (see apiClient) and let the next one
			// try silently again. Sending it without a token would come back as a misleading 401.
			// Anything else (session expired, consent, a renewal that timed out) goes to sign-in as before.
			if (isNetworkAuthError(error)) {
				throw new Error('Could not refresh your sign-in. Check your internet connection and try again.');
			}

			try {
				await instance.acquireTokenRedirect({
					...apiRequest,
					account: currentAccount,
				});
				return null;
			} catch (redirectError) {
				console.error('❌ [AuthContext] Failed to acquire token with redirect:', redirectError);
				return null;
			}
		}
	}, [instance]);

	/**
	 * Fetch user profile from backend API
	 * Token is automatically added by apiClient interceptor
	 */
	const fetchUserProfile = useCallback(async () => {
		if (!isAuthenticated || !account) {
			setUserProfile(null);
			setIsLoading(false);
			return;
		}

		setIsLoading(true);
		setError(null);

		// Retry a few times: the API can be briefly unreachable after a rebuild or a cold start, and the
		// one-shot bootstrap would otherwise leave the app with no profile until a manual refresh.
		const maxAttempts = 5;
		for (let attempt = 1; attempt <= maxAttempts; attempt++) {
			try {
				// Token is automatically added by apiClient interceptor
				const response = await apiClient.get<UserProfile>('/api/users/me');
				setUserProfile(response.data);
				setIsLoading(false);
				return;
			} catch (err) {
				// No response => network/connection error (API not up yet); otherwise inspect status.
				const status = (err as { response?: { status?: number } }).response?.status;
				const isTransient =
					status === undefined || status === 404 || status === 502 || status === 503 || status === 504;

				if (attempt < maxAttempts && isTransient) {
					// Exponential backoff, capped: 0.5s, 1s, 2s, 4s.
					const delayMs = Math.min(500 * 2 ** (attempt - 1), 4000);
					await new Promise((resolve) => setTimeout(resolve, delayMs));
					continue;
				}

				console.error('Failed to fetch user profile:', err);
				setError(describeProfileError(err));
				setUserProfile(null);
				setIsLoading(false);
				return;
			}
		}
	}, [isAuthenticated, account]);

	const refreshUserProfile = useCallback(async () => {
		await fetchUserProfile();
	}, [fetchUserProfile]);

	/**
	 * Login with Azure AD (redirect to Microsoft login page)
	 */
	const login = useCallback(async () => {
		try {
			setError(null);

			await instance.loginRedirect({
				...apiRequest,
			});
		} catch (err) {
			// Ignore interaction_in_progress errors (redirect already in progress)
			if (err instanceof Error && err.message.includes('interaction_in_progress')) {
				console.log('Login redirect already in progress, ignoring...');
				return;
			}

			console.error('Login failed:', err);
			setError(err instanceof Error ? err.message : 'Login failed');
		}
	}, [instance]);

	/**
	 * Logout from Azure AD
	 */
	const logout = useCallback(async () => {
		try {
			setError(null);
			setUserProfile(null);
			await instance.logoutRedirect({
				account,
			});
		} catch (err) {
			console.error('Logout failed:', err);
			setError(err instanceof Error ? err.message : 'Logout failed');
			throw err;
		}
	}, [instance, account]);

	// Combine Azure AD account info with backend user profile
	const user: User | null = useMemo(() => {
		if (!account && !userProfile) return null;

		return {
			azureAdId: account?.localAccountId,
			email: userProfile?.email || account?.username,
			displayName: userProfile?.displayName || account?.name,
			userId: userProfile?.id,
			fullName: userProfile?.fullName,
			roles: userProfile?.roles,
			sites: userProfile?.sites,
			isActive: userProfile?.isActive,
		};
	}, [account, userProfile]);

	// Set token provider for API client
	useEffect(() => {
		setTokenProvider(getAccessToken);
	}, [getAccessToken]);

	useEffect(() => {
		if (isAuthenticated && account) {
			fetchUserProfile();
		} else {
			setUserProfile(null);
			setIsLoading(false);
		}
	}, [isAuthenticated, account, fetchUserProfile]);

	const value = useMemo(
		() => ({
			isAuthenticated,
			isLoading,
			account,
			userProfile,
			user,
			error,
			login,
			logout,
			getAccessToken,
			refreshUserProfile,
		}),
		[isAuthenticated, isLoading, account, userProfile, user, error, login, logout, getAccessToken, refreshUserProfile],
	);

	return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * Hook to use authentication context
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useAuth() {
	const context = useContext(AuthContext);
	if (!context) {
		throw new Error('useAuth must be used within AuthProvider');
	}
	return context;
}

export default AuthContext;
